<template>
  <div class="relative">
    <div
      class="flex h-12 w-12 cursor-pointer select-none items-center justify-center rounded-full bg-primary shadow-lg transition-all duration-200"
      @click.stop="
        isChatOpen = !isChatOpen;
        $nextTick(() => scrollToBottom());
      "
    >
      <Icon name="chat_bubble" class="text-xl text-white"> </Icon>
    </div>
    <transition
      enter-active-class="transition-all duration-200 ease-out"
      leave-active-class="transition-all duration-150 ease-in"
      enter-from-class="opacity-0 scale-95 translate-y-2"
      enter-to-class="opacity-100 scale-100 translate-y-0"
      leave-from-class="opacity-100 scale-100 translate-y-0"
      leave-to-class="opacity-0 scale-95 translate-y-2"
    >
      <div
        v-if="isChatOpen"
        class="absolute bottom-[3.5rem] right-0 flex h-[32rem] w-96 flex-col rounded-2xl border border-gray-200 bg-white shadow-2xl"
      >
        <div
          class="flex h-16 w-full items-center justify-between rounded-t-2xl border-b border-zinc-200 bg-zinc-100 p-4 text-black"
        >
          <div class="flex items-center justify-center gap-x-4">
            <Icon name="smart_toy" class="!text-3xl"> </Icon>
            <span class="font-medium">{{
              __('Your Personal Career Advisor')
            }}</span>
          </div>
          <Icon
            @click.stop="
              isChatOpen = false;
              $nextTick(() => scrollToBottom());
            "
            name="clear"
            class="cursor-pointer !text-2xl"
          ></Icon>
        </div>
        <!-- Messages container -->
        <div
          ref="chatRef"
          class="scrollbar-thin mb-4 flex-1 space-y-4 overflow-y-auto overscroll-contain bg-white p-4"
        >
          <div
            v-for="(msg, index) in messages"
            :key="index"
            class="flex items-center gap-x-2 text-sm"
            :class="msg.role === 'user' ? 'justify-end' : 'justify-start'"
          >
            <Icon v-if="msg.role !== 'user'" name="smart_toy" class="!text-3xl">
            </Icon>
            <div
              :class="[
                'inline-block max-w-[80%] rounded-xl p-3',
                msg.role === 'user'
                  ? 'bg-zinc-200 text-black'
                  : 'bg-zinc-100 text-black'
              ]"
              v-html="msg.content"
            ></div>
          </div>

          <!-- Loading indicator -->
          <div
            v-if="loadingMessage"
            class="flex items-center justify-start gap-x-2"
          >
            <Icon name="smart_toy" class="!text-3xl"> </Icon>
            <!-- <Icon class="animate-spin !text-xl" name="progress_activity" /> -->

            <div class="flex gap-1 rounded-xl bg-zinc-100 px-2 py-4">
              <span
                class="typing-dot h-1.5 w-1.5 rounded-full bg-zinc-400"
                style="animation-delay: 0s"
              ></span>
              <span
                class="typing-dot h-1.5 w-1.5 rounded-full bg-zinc-400"
                style="animation-delay: 0.15s"
              ></span>
              <span
                class="typing-dot h-1.5 w-1.5 rounded-full bg-zinc-400"
                style="animation-delay: 0.3s"
              ></span>
            </div>
          </div>
        </div>

        <!-- Input -->
        <div
          class="flex items-center justify-between border-t border-zinc-200 px-4 py-2"
        >
          <input
            v-model="inputMessage"
            @keyup.enter="handleAiCareerPathMessage"
            :disabled="loadingMessage"
            type="text"
            placeholder="Type a message..."
            class="w-full resize-none border-0 bg-transparent py-2 focus:outline-none focus:ring-0"
          />

          <Icon
            @click="handleAiCareerPathMessage"
            :disabled="loadingMessage"
            name="send"
            class="cursor-pointer !text-2xl text-gray-600 disabled:opacity-50"
          >
          </Icon>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
  import Icon from '@/components/global/Icon.vue';
  import { __ } from '@/helpers';
  import { nextTick, ref } from 'vue';
  import { useToast } from 'vue-toastification';

  const isChatOpen = ref(false);
  const toast = useToast();
  const messages = ref([
    {
      role: 'assistant',
      content:
        `<strong>` +
        __(`Hello! I'm your Career Assistant.`) +
        `</strong><br>
` +
        __(`Complete your CV for personalized career advice.`) +
        `<br><br>
<strong>` +
        __(`You can ask me about:`) +
        `</strong><br>
` +
        __(
          `Matching jobs • Skills to develop • New career paths • CV improvements`
        ) +
        ``
    }
  ]);
  const inputMessage = ref('');
  const loadingMessage = ref(false);

  const handleAiCareerPathMessage = async () => {
    if (!inputMessage.value.trim()) return;
    try {
      messages.value.push({
        role: 'user',
        content: inputMessage.value
      });
      scrollToBottom();
      inputMessage.value = '';
      loadingMessage.value = true;

      const response = await axios.post(
        route('redesign.ai-career-path', {
          history: messages.value
        })
      );

      if (response.data && response.data.message) {
        messages.value.push({
          role: 'assistant',
          content: response.data.message
        });
      }
    } catch (error) {
      messages.value.push({
        role: 'assistant',
        content:
          'Sorry, there was an error processing your request. Please try again later.'
      });

      toast.error(
        error.response?.data?.message ||
          'An error occurred. Please try again later.'
      );
    } finally {
      loadingMessage.value = false;
    }
  };

  const chatRef = ref(null);

  const scrollToBottom = () => {
    nextTick(() => {
      if (chatRef.value) {
        chatRef.value.scrollTo({
          top: chatRef.value.scrollHeight,
          behavior: 'smooth'
        });
      }
    });
  };
</script>

<style>
  @keyframes typingDot {
    0%,
    70%,
    100% {
      transform: translateY(0);
      opacity: 0.6;
    }
    35% {
      transform: translateY(-3px);
      opacity: 1;
    }
  }

  .typing-dot {
    animation: typingDot 1.5s ease-in-out infinite;
  }
</style>
